import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie';
import { AvantoLambdaService } from './avanto-lambda.service';
import { SessionObject } from '../avantoeats-util/models/session/SessionObject';
import { Subject, BehaviorSubject } from 'rxjs';
import { SessionCustomerAddress } from '../avantoeats-util/models/session/SessionCustomerAddress';
import { SessionCustomerInfo } from '../avantoeats-util/models/session/SessionCustomerInfo';

@Injectable({
  providedIn: 'root'
})
export class AvantoSessionService {

  private readonly SESSION_COOKIE_NAME = "avanto-session-id";
  private currentSession: SessionObject;

  sessionSubject: Subject<SessionObject> = new BehaviorSubject(null);
  orderDetailsChangedSubject:Subject<boolean> = new Subject();
  orderTimeChangedSubject: Subject<boolean> = new Subject();
  orderAddressChangedSubject: Subject<SessionCustomerAddress> = new BehaviorSubject(null);

  constructor(private cookieService: CookieService, private lambdaService: AvantoLambdaService) { }

  /** Gets the user session from the database. If no session exists, creates one.*/
  getSession() {
    return new Promise<SessionObject>((resolve, reject) => {
      //Gets a session object from the database using the sessionId stored in a cookie
      const sessionId = this.cookieService.get(this.SESSION_COOKIE_NAME);
      this.lambdaService.getSession(sessionId).subscribe((response) => {
        if (response.ok) {
          //Converts the session retrieved from amazon into an object and fires the session subject
          const retrievedSession = this.updateSession(response);
          //If we didn't have a cookie with a sessionId before, we create one now
          //If or the server returns a new sessionId (when session expires), the cookie is updated
          if (sessionId == null || sessionId != retrievedSession.sessionId) {
            this.cookieService.put(this.SESSION_COOKIE_NAME, retrievedSession.sessionId)
          }
          resolve(retrievedSession);
        } else {
          console.error("Unable to get the session: ", response)
          reject();
        }
      },
        (error) => {
          console.error("Error while trying to get the session: ", error)
          reject();
        });
    })
  }

  removeSessionFromCookie(){
    this.cookieService.remove(this.SESSION_COOKIE_NAME);
  }

  /** Clears the session and gets a new one */
  clearSession() {
    return new Promise<void>((resolve, reject) => {
      this.lambdaService.removeAllItemsFromSession(this.currentSession.sessionId).subscribe((response) => {
        if (response.ok) {
          this.updateSession(response);
          resolve();
        } else {
          console.error("Unable to update the session: ", response)
          reject();
        }
      },
        (error) => {
          console.error("Error while trying to update the session: ", error)
          reject();
        });
    });
  }

  /**
   * Sets a new cart item in the session
   * @param productId ID of the product being added
   * @param productQuantity How many of the product is being added
   * @param productVersion Version of the product being added
   * @param aditionalNotes Additional notes that can be added for the product
   */
  setItemInSession(
    productId: string,
    restaurantId: string,
    productQuantity: number,
    productVersion: number | null,
    additionalNotes: string,
    productCustomizations: { customizationId: string, selectedOptions: { optionId: string, quantity: number, toppingSide:"left"|"full"|"right"|null }[] }[],
    cartItemId: string | null
  ) {
    return new Promise<void>((resolve, reject) => {
      this.lambdaService.setItemInSession(
        this.currentSession.sessionId,
        restaurantId,
        productId, productQuantity,
        productVersion,
        additionalNotes,
        productCustomizations,
        cartItemId
      ).subscribe((response) => {
        if (response.ok) {
          this.updateSession(response);
          resolve();
        } else {
          console.error("Unable to update the session: ", response)
          reject();
        }
      },
        (error) => {
          console.error("Error while trying to update the session: ", error)
          reject();
        });
    });
  }

  /** Updates the customer session by removing an item from it and recalculating the totals */
  removeItemFromSession(cartItemIds:string[]) {
    return new Promise<void>((resolve, reject) => {
      this.lambdaService.removeItemFromSession(this.currentSession.sessionId, cartItemIds).subscribe((response) => {
        if (response.ok) {
          this.updateSession(response);
          resolve();
        } else {
          console.error("Unable to update the session: ", response)
          reject();
        }
      },
        (error) => {
          console.error("Error while trying to update the session: ", error)
          reject();
        });
    });
  }

  /** Checks if the session is valid. Invalid sessions cannot be used for checkout. */
  validateSession() {
    return new Promise<{session:any,error:{errorCode:number, errorResponse:any}}>((resolve, reject) => {
      this.lambdaService.validateSession(this.currentSession.sessionId).subscribe((response) => {
        if (response.ok) {
          // this.updateSession(response);
          resolve(response.json().data);
        } else {
          console.error("Unable to update the session: ", response)
          reject();
        }
      },
        (error) => {
          console.error("Error while trying to update the session: ", error)
          reject();
        });
    });
  }

  /** Sets a new customer address on the session */
  setCustomerAddress(address:string, latitude: number, longitude: number, addressNote?: string) {
    return new Promise<SessionObject>((resolve, reject) => {
      const sessionId = this.currentSession.sessionId;
      this.lambdaService.updateSessionAddress(sessionId, address, addressNote, latitude, longitude).subscribe(
        (response) => {
          const retrievedSession = this.updateSession(response, true);
          this.orderDetailsChangedSubject.next(true);
          resolve(retrievedSession);
        },
        (error) => {
          console.error("Error while trying to update the address", error)
          reject()
        }
      );
    })
  }

  /** Sets a the customer information on the session */
  setCustomerInfo(customerInfo: SessionCustomerInfo) {
    return new Promise<void>((resolve, reject) => {
      const sessionId = this.currentSession.sessionId;
      this.lambdaService.updateSessionCustomerInfo(sessionId, customerInfo.toAwsObject()).subscribe(
        (response) => {
          this.updateSession(response, true);
          resolve();
        },
        (error) => {
          console.error("Error while trying to update the address", error)
          reject()
        }
      );
    })
  }

  /** Sets a new customer address on the session */
  editAddressNote(addressNote: string) {
    return new Promise<void>((resolve, reject) => {
      const sessionId = this.currentSession.sessionId;
      const addr = this.currentSession.customerAddress.address;
      const lat = this.currentSession.customerAddress.lat;
      const lng = this.currentSession.customerAddress.lng;
      this.lambdaService.updateSessionAddress(sessionId, addr, addressNote, lat, lng).subscribe(
        (response) => {
          this.updateSession(response, true);
          resolve();
        },
        (error) => {
          console.error("Error while trying to update the address", error)
          reject()
        }
      );
    })
  }

  /** Sets an order type on the session */
  setOrderType(sessionId, newOrderType) {
    return new Promise<void>((resolve, reject) => {
      this.lambdaService.updateSessionOrderType(sessionId, newOrderType).subscribe(
        (response) => {
          this.updateSession(response);
          this.orderDetailsChangedSubject.next(true);
          resolve();
        }, (error) => {
          console.error(error)
          reject()
        });
    });
  }

  /**
   * Checks if the restaurant is available on the date/time selected on the session
   */
  checkIfRestaurantAvailable(){
    return new Promise((resolve, reject) => {
      const sessionId = this.currentSession.sessionId;
      this.lambdaService.checkIfRestaurantAvailable(sessionId).subscribe(
        (response) => {
          resolve(response.json().data);
        },
        (error) => {
          console.error("Error while trying to check restaurant availability", error)
          reject()
        }
      );
    })
  }

  /** Sets a delivery time on the session */
  setOrderTime(sessionId, delTime, delTimestamp?) {
    return new Promise<void>((resolve, reject) => {
      this.lambdaService.updateSessionOrderTime(sessionId, delTime, delTimestamp).subscribe(
        (response) => {
          this.updateSession(response);
          this.orderTimeChangedSubject.next(true);
          this.orderDetailsChangedSubject.next(true);
          resolve();
        }, (error) => {
          console.error(error)
          reject()
        });
    });
  }

  // /**
  //  * Pays for an order using square
  //  * @param sessionId ID of the session, saved on the server
  //  * @param accessTokenAccount a string representing the account where the payment is supposed to go to. 
  //  * The server will interpret this variable and send the funds to the appropriate account accordingly
  //  */
  // processSquarePayment(sourceId:string, squareCustomerId:string, accessTokenAccount:string, isTesting:boolean) {
  //   return new Promise((resolve, reject) => {
  //     this.lambdaService.processSquarePayment(this.currentSession.sessionId, sourceId, squareCustomerId, accessTokenAccount, isTesting).subscribe(
  //       (response) => {
  //         resolve();
  //       }, (err) => {
  //         console.error(JSON.parse(err._body).error)
  //         reject()
  //       });
  //   });
  // }

  /**
   * Sets a tip to the order
   * @param value how much will be given as a tip
   */
  setTip(value: number){
    return new Promise<void>((resolve, reject) => {
      this.lambdaService.setTip(this.currentSession.sessionId,value).subscribe((response: any) => {
        this.updateSession(response);
        resolve();
      },
        (error) => {
          console.error("Error while trying to set a tip: ", JSON.parse(error._body).error)
          reject();
        });
    });
  }

  /**
   * Applies a coupon to the order
   * @param couponCode code of the coupon
   */
  applyCoupon(couponCode: string){
    return new Promise<void>((resolve, reject) => {
      this.lambdaService.applyCoupon(this.currentSession.sessionId,couponCode).subscribe((response: any) => {
        this.updateSession(response);
        resolve();
      },
        (error) => {
          console.error("Error while trying to apply a coupon: ",error)
          reject(error);
        });
    });
  }

  /**
   * Removes the coupon added to the order
   * @param couponCode code of the coupon
   */
  removeCoupon(){
    return new Promise<void>((resolve, reject) => {
      this.lambdaService.removeCoupon(this.currentSession.sessionId).subscribe((response: any) => {
        this.updateSession(response);
        resolve();
      },
        (error) => {
          const err = JSON.parse(error._body).error
          console.error("Error while trying to apply a coupon: ", err)
          reject(err);
        });
    });
  }

  /**
   * Makes the checkout of the current session
   * 
   * @param paymentMethod 
   */
  checkout(paymentMethod:string, creditCardLast4Digits:string) {
    return new Promise((resolve, reject) => {
      this.lambdaService.checkout(this.currentSession.sessionId, paymentMethod, creditCardLast4Digits).subscribe(
        (response) => {
          resolve(response.json().data);
        }, (error) => {
          console.error(error)
          reject()
        });
    });
  }

  /**
   * TODO <==
   * @param restaurantId 
   * @param cartInfo 
   */
  reOrderCart(restaurantId, cartInfo) {
    return new Promise<void>((resolve, reject) => {
      this.lambdaService.reorderCart(this.currentSession.sessionId, restaurantId, cartInfo).subscribe(
        response => {
          this.updateSession(response);
          resolve();
        },
        error => {
          const errorStr = error.json().error;
          console.error("Could not place a cart re-order: ", errorStr)
          reject(errorStr)
        }
      )
    });
  }

  updateOneRestaurantModeInfo(useOneRestaurantMode:boolean, restaurantId?:string){
    return new Promise<void>((resolve, reject)=>{
      this.lambdaService.updateOneRestaurantModeInfo(this.currentSession.sessionId, useOneRestaurantMode, restaurantId).subscribe(
        response=>{
          this.updateSession(response);
          resolve()
        },
        error => {
          console.error("Could not change one restaurant mode info: ", error)
          reject()
        }
      )
    })
  }

  isCartEmpty() {
    return this.currentSession.cart.cartItems.size == 0;
  }
  
  requestSessionUpdate(awsResponse, addressChanged:boolean, orderDetailsChanged:boolean){
    this.currentSession = SessionObject.newfromAwsData(awsResponse);
    this.sessionSubject.next(this.currentSession);
    if(addressChanged){
      this.orderAddressChangedSubject.next();
    }
    if(orderDetailsChanged){
      this.orderDetailsChangedSubject.next();
    }
  }

  private updateSession(awsResponse, addressChanged?: boolean): SessionObject {
    const isFirstUpdate = this.currentSession == null;

    this.currentSession = SessionObject.newfromAwsData(awsResponse.json().data);
    this.sessionSubject.next(this.currentSession);
    if (isFirstUpdate || addressChanged) {
      this.orderAddressChangedSubject.next(this.currentSession.customerAddress);
      this.orderDetailsChangedSubject.next();
    }
    return this.currentSession;
  }
}